import { useState, useRef, useCallback, useMemo } from "react";
import { useIonAlert, useIonToast } from "@ionic/react";
import { SUPPORT_MONKEY_UID, filterOutSupportMonkey } from "../../constants/supportConstants";
import { createAssociateSearchFilter, sortAssociatesByName } from "../../utils/associateFilters";

export interface AssociatesData {
  WerkerUid: string;
  WerkerFirstName: string;
  WerkerLastName: string;
  WerkerCompany: string;
  WerkerProfilePicURL: string;
  WerkerBio: string;
  isInvited: boolean;
  DocId: string;
}

interface UseWerkerSelectionProps {
  initialAssociates?: AssociatesData[];
}

/**
 * Hook for managing werker selection state and handlers
 * Used by both SelectWerkers.tsx and selectWerkersSeries.tsx
 */
export const useWerkerSelection = ({ initialAssociates = [] }: UseWerkerSelectionProps = {}) => {
  const [presentAlert] = useIonAlert();
  const [presentToast] = useIonToast();

  // Search & UI state
  const [searchText, setSearchText] = useState("");
  const [crewAccordionValue, setCrewAccordionValue] = useState<string | undefined>(undefined);

  // Associates state
  const [initialAssociatesCrew, setInitialAssociatesCrew] = useState<AssociatesData[]>(initialAssociates);
  const [associatesCrew, setAssociatesCrew] = useState<AssociatesData[]>(initialAssociates);

  // Scroll preservation
  const contentRef = useRef<HTMLIonContentElement | null>(null);
  const savedScrollY = useRef<number>(0);
  const rafId = useRef<number | null>(null);

  const onContentScroll = useCallback((e: CustomEvent) => {
    // @ts-ignore ionic typing for detail.scrollTop
    savedScrollY.current = e.detail?.scrollTop ?? 0;
  }, []);

  const restoreScroll = useCallback(() => {
    if (!contentRef.current) return;
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      contentRef.current?.scrollToPoint(0, savedScrollY.current, 0);
    });
  }, []);

  // Filter + sort (memoized) — ignore blank UIDs and support monkey
  const filteredSorted = useMemo(() => {
    const base = filterOutSupportMonkey(associatesCrew.filter((v) => !!v.WerkerUid));
    const matchesSearch = createAssociateSearchFilter(searchText || "");
    const filtered = base.filter(matchesSearch);
    return sortAssociatesByName(filtered);
  }, [associatesCrew, searchText]);

  // Selection state
  const allFilteredSelected = useMemo(() => {
    return filteredSorted.length > 0 && filteredSorted.every((x) => x.isInvited === true);
  }, [filteredSorted]);

  const someFilteredSelected = useMemo(() => {
    if (filteredSorted.length === 0) return false;
    const selectedCount = filteredSorted.filter((x) => x.isInvited === true).length;
    return selectedCount > 0 && selectedCount < filteredSorted.length;
  }, [filteredSorted]);

  const visibleUidsCount = useMemo(() => filteredSorted.length, [filteredSorted]);

  const visibleSelectedCount = useMemo(() => {
    return filteredSorted.filter((x) => x.isInvited === true).length;
  }, [filteredSorted]);

  const totalUidsCount = useMemo(() => associatesCrew.filter((x) => !!x.WerkerUid).length, [associatesCrew]);

  const totalSelectedCount = useMemo(() => associatesCrew.filter((x) => x.isInvited === true).length, [associatesCrew]);

  // Handlers
  const applySelectAll = useCallback(
    (checked: boolean) => {
      const filteredUIDs = new Set(filteredSorted.map((u) => u.WerkerUid));
      restoreScroll();
      setAssociatesCrew((prev) => prev.map((u) => (filteredUIDs.has(u.WerkerUid) ? { ...u, isInvited: checked } : u)));
      restoreScroll();
    },
    [filteredSorted, restoreScroll]
  );

  const handleSelectAllClick = useCallback(() => {
    applySelectAll(!allFilteredSelected);
  }, [applySelectAll, allFilteredSelected]);

  const handleCrewList = useCallback(
    (uid: string, e: any) => {
      const checked =
        (e?.detail && typeof e.detail.checked === "boolean" && e.detail.checked) ??
        (typeof e?.target?.checked === "boolean" && e.target.checked) ??
        false;

      restoreScroll();
      setAssociatesCrew((prev) => prev.map((u) => (u.WerkerUid === uid ? { ...u, isInvited: checked } : u)));
      restoreScroll();
    },
    [restoreScroll]
  );

  const applyCrewToSelection = useCallback(
    async (crew: any) => {
      const crewName = (crew?.name || "").trim() || "this crew";
      const rawIds = Array.isArray(crew?.memberIds) ? crew.memberIds : [];

      const crewIds = rawIds
        .filter((v: any) => typeof v === "string" && v.length > 0)
        .filter((id: string) => id !== SUPPORT_MONKEY_UID);

      if (crewIds.length === 0) {
        await presentAlert({
          header: "Crew is Empty",
          message: `"${crewName}" has no members yet.`,
          buttons: ["OK"],
        });
        return;
      }

      await presentAlert({
        header: "Schedule Whole Crew?",
        message: `Schedule all members of "${crewName}" on this job?`,
        buttons: [
          { text: "Cancel", role: "cancel" },
          {
            text: "Schedule Crew",
            handler: () => {
              restoreScroll();

              let addedCount = 0;
              const set = new Set(crewIds);

              setAssociatesCrew((prev) =>
                prev.map((u) => {
                  if (set.has(u.WerkerUid) && !u.isInvited) {
                    addedCount += 1;
                    return { ...u, isInvited: true };
                  }
                  return u;
                })
              );

              restoreScroll();
              setCrewAccordionValue(undefined);

              presentToast({
                message: `${crewName} Selected`,
                duration: 1500,
                position: "top",
                color: "medium",
              });
            },
          },
        ],
      });
    },
    [presentAlert, presentToast, restoreScroll]
  );

  const getInitials = useCallback((name: string) => {
    const parts = (name || "").trim().split(" ").filter(Boolean);
    if (parts.length === 0) return "C";
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || "C";
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, []);

  return {
    // State
    searchText,
    setSearchText,
    crewAccordionValue,
    setCrewAccordionValue,
    initialAssociatesCrew,
    setInitialAssociatesCrew,
    associatesCrew,
    setAssociatesCrew,
    
    // Scroll
    contentRef,
    onContentScroll,
    restoreScroll,
    
    // Filtered/sorted data
    filteredSorted,
    
    // Selection state
    allFilteredSelected,
    someFilteredSelected,
    visibleUidsCount,
    visibleSelectedCount,
    totalUidsCount,
    totalSelectedCount,
    
    applySelectAll,
    handleSelectAllClick,
    handleCrewList,
    applyCrewToSelection,
    
    // Utils
    filterOutSupportMonkey,
    getInitials,
  };
};
